import {
  Activity,
  AtSign,
  Award,
  BedDouble,
  BookCheck,
  BookMarked,
  BookOpen,
  Bookmark,
  Brain,
  CalendarCheck,
  CalendarDays,
  Camera,
  ChefHat,
  ClipboardList,
  ClipboardPlus,
  Download,
  Droplets,
  FileBarChart,
  FileText,
  Filter,
  Flag,
  Flame,
  FlaskConical,
  Flower2,
  Folder,
  Footprints,
  GitCompare,
  GraduationCap,
  Handshake,
  Heart,
  HeartPulse,
  History,
  Images,
  LayoutDashboard,
  Library,
  LineChart,
  ListChecks,
  Map as MapIcon,
  MessagesSquare,
  Microscope,
  Newspaper,
  NotebookPen,
  PersonStanding,
  PieChart,
  Pill,
  Receipt,
  Salad,
  ScrollText,
  Settings,
  Shield,
  Smile,
  Stethoscope,
  Sun,
  Sunrise,
  Tablets,
  TestTube,
  Thermometer,
  TrendingUp,
  Trophy,
  Upload,
  User,
  UserRoundCheck,
  UserSearch,
  UsersRound,
  Users,
  Utensils,
  Video,
  Wind,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { useMemo } from "react";

import { useSiteFeatureFlags } from "@shared/hooks/use-site-feature-flags";
import { isFeatureOn, type SiteFeatureFlags } from "@shared/lib/site-features";

export interface EcosystemFeature {
  /** Translation key under `ecosystem.categories.<cat>.features.<key>` */
  key: string;
  icon: LucideIcon;
  /**
   * Site feature this card advertises (`apps/core/features/registry.py`).
   * Omitted for core surfaces, which are always shown. Marketing never
   * advertises a disabled feature.
   */
  feature?: string;
}

export interface EcosystemCategory {
  /** Translation key under `ecosystem.categories.<key>` (title, subtitle) */
  key: string;
  icon: LucideIcon;
  /** Desktop grid column span (1 or 2). Tracking + Insights span 2 for rhythm. */
  span: 1 | 2;
  features: EcosystemFeature[];
}

/**
 * Mirrors the authenticated app's sidebar groups in
 * `api/apps/_shared_frontend/layouts/system-shell/SystemShell.tsx`.
 *
 * The "89 features" marketing claim reflects the full sidebar inventory;
 * this list surfaces the most marketable highlights per category.
 */
export const FEATURE_CATEGORIES: EcosystemCategory[] = [
  {
    key: "tracking",
    icon: HeartPulse,
    span: 2,
    features: [
      { key: "symptoms", icon: HeartPulse, feature: "symptoms" },
      { key: "symptomLog", icon: NotebookPen, feature: "symptoms.log" },
      { key: "checkin", icon: CalendarCheck, feature: "ai_coaching.check_in" },
      { key: "history", icon: History, feature: "symptoms.history" },
      { key: "flareups", icon: Thermometer, feature: "symptoms.flare_ups" },
      { key: "conditions", icon: ClipboardPlus, feature: "symptoms.conditions" },
      { key: "bodyMap", icon: PersonStanding, feature: "symptoms.body_map" },
      { key: "photos", icon: Camera, feature: "symptoms.photos" },
      { key: "experiments", icon: FlaskConical, feature: "experiments" },
    ],
  },
  {
    key: "treatment",
    icon: Pill,
    span: 1,
    features: [
      { key: "supplements", icon: Pill, feature: "supplements" },
      { key: "coimbra", icon: Sun, feature: "supplements.coimbra" },
      { key: "thiamine", icon: Zap, feature: "supplements.thiamine" },
      { key: "supplementLog", icon: ListChecks, feature: "supplements.log" },
      { key: "effectiveness", icon: TrendingUp, feature: "supplements.effectiveness" },
      { key: "treatments", icon: ClipboardList, feature: "treatments" },
      { key: "costs", icon: Receipt, feature: "treatments.costs" },
      { key: "history", icon: ScrollText, feature: "treatments.history" },
    ],
  },
  {
    key: "insights",
    icon: Brain,
    span: 2,
    features: [
      { key: "analytics", icon: LineChart, feature: "analytics" },
      { key: "aiInsights", icon: Brain, feature: "ai_coaching" },
      { key: "patterns", icon: LineChart, feature: "ai_coaching.patterns" },
      { key: "coaching", icon: MessagesSquare, feature: "ai_coaching.coaching" },
      { key: "compare", icon: GitCompare, feature: "analytics.compare" },
      { key: "milestones", icon: Trophy, feature: "ai_coaching.milestones" },
      { key: "streaks", icon: Flame, feature: "analytics.streaks" },
      { key: "badges", icon: Award, feature: "analytics.badges" },
      { key: "reports", icon: FileBarChart, feature: "analytics.reports" },
      { key: "progressPhotos", icon: Images, feature: "analytics.photos" },
    ],
  },
  {
    key: "diet",
    icon: Utensils,
    span: 1,
    features: [
      { key: "dashboard", icon: Salad, feature: "diet" },
      { key: "foodLog", icon: Utensils, feature: "diet.food_log" },
      { key: "protocols", icon: MapIcon, feature: "diet.protocols" },
      { key: "elimination", icon: Filter, feature: "diet.elimination" },
      { key: "fatRatio", icon: PieChart, feature: "diet.fat_ratio" },
      { key: "electrolytes", icon: Droplets, feature: "diet.electrolytes" },
      { key: "mealPlanner", icon: CalendarDays, feature: "diet.meal_planner" },
    ],
  },
  {
    key: "lifestyle",
    icon: Activity,
    span: 1,
    features: [
      { key: "stress", icon: Wind, feature: "lifestyle.stress" },
      { key: "meditation", icon: Flower2, feature: "lifestyle.meditation" },
      { key: "sleep", icon: BedDouble, feature: "lifestyle.sleep" },
      { key: "mood", icon: Smile, feature: "lifestyle.mood" },
      { key: "activities", icon: Footprints, feature: "lifestyle.activities" },
      { key: "milestones", icon: Flag, feature: "lifestyle.milestones" },
    ],
  },
  {
    key: "medical",
    icon: Stethoscope,
    span: 1,
    features: [
      { key: "labs", icon: TestTube, feature: "medical.labs" },
      { key: "visits", icon: UserRoundCheck, feature: "medical.visits" },
      { key: "medications", icon: Tablets, feature: "medical.medications" },
      { key: "reports", icon: FileText, feature: "medical.reports" },
    ],
  },
  {
    key: "learn",
    icon: BookOpen,
    span: 1,
    features: [
      { key: "knowledge", icon: BookOpen, feature: "knowledge" },
      { key: "books", icon: Library, feature: "knowledge.books" },
      { key: "studies", icon: Microscope, feature: "knowledge.studies" },
      { key: "protocols", icon: BookMarked, feature: "knowledge.protocols" },
      { key: "readingLists", icon: BookCheck, feature: "knowledge.reading_lists" },
      { key: "videos", icon: Video, feature: "knowledge.videos" },
    ],
  },
  {
    key: "community",
    icon: Users,
    span: 2,
    features: [
      { key: "hopeFeed", icon: Sunrise, feature: "community.hope" },
      { key: "gratitude", icon: Heart, feature: "community.gratitude" },
      { key: "similar", icon: UsersRound, feature: "community.similar_users" },
      { key: "mentors", icon: GraduationCap, feature: "community.mentors" },
      { key: "matches", icon: Handshake, feature: "community.mentor_matches" },
      { key: "stories", icon: Newspaper, feature: "community.stories" },
      { key: "recipes", icon: ChefHat, feature: "community.recipes" },
      { key: "saved", icon: Bookmark, feature: "community.saved" },
      { key: "mentions", icon: AtSign, feature: "community.mentions" },
      { key: "search", icon: UserSearch, feature: "community.search" },
    ],
  },
  {
    key: "overview",
    icon: LayoutDashboard,
    span: 1,
    features: [
      { key: "dashboard", icon: LayoutDashboard, feature: "overview.dashboard" },
      { key: "exports", icon: Download, feature: "overview.export" },
    ],
  },
  {
    key: "account",
    icon: Shield,
    span: 1,
    features: [
      { key: "profile", icon: User },
      { key: "files", icon: Folder },
      { key: "upload", icon: Upload },
      { key: "settings", icon: Settings },
    ],
  },
];

/**
 * Categories and cards for the features that are actually enabled on this
 * deployment. Cards of disabled features are dropped and a category with no
 * card left disappears — the landing page never advertises what the app
 * does not expose. Fail closed: no flag map ⇒ only core cards.
 */
export function visibleEcosystemCategories(
  flags: SiteFeatureFlags | null | undefined,
  categories: readonly EcosystemCategory[] = FEATURE_CATEGORIES,
): EcosystemCategory[] {
  return categories
    .map((category) => ({
      ...category,
      features: category.features.filter(
        (feature) => !feature.feature || isFeatureOn(flags, feature.feature),
      ),
    }))
    .filter((category) => category.features.length > 0);
}

export function countEcosystemFeatures(categories: readonly EcosystemCategory[]): number {
  return categories.reduce((total, category) => total + category.features.length, 0);
}

/** Flag-aware cards + counts for the landing sections (hero + ecosystem). */
export function useEcosystemCategories(): {
  categories: EcosystemCategory[];
  featureCount: number;
  categoryCount: number;
} {
  const { data: flags } = useSiteFeatureFlags();
  return useMemo(() => {
    const categories = visibleEcosystemCategories(flags);
    return {
      categories,
      featureCount: countEcosystemFeatures(categories),
      categoryCount: categories.length,
    };
  }, [flags]);
}
